import { useState, type FormEvent } from 'react';
import { Button, Card, ErrorNotice, Field, Icon, StatusPill, Toggle, type Status } from '@hive/ui';
import { api, errorMessage, type CrmSource, type CrmSyncResult } from '../api/client';
import { useLoad, useNow } from '../api/hooks';
import { ago } from '../format';
import { Dialog } from './Dialog';

const STATUS: Record<NonNullable<CrmSource['lastStatus']>, { label: string; status: Status }> = {
  ok: { label: 'Last pull fine', status: 'healthy' },
  partial: { label: 'Some records failed', status: 'expiring' },
  failed: { label: 'Last pull failed', status: 'offline' },
};

const counts = (c: { created: number; updated: number; unchanged: number; failed: number }) =>
  [c.created && `${c.created} new`, c.updated && `${c.updated} updated`, c.unchanged && `${c.unchanged} unchanged`, c.failed && `${c.failed} failed`].filter(Boolean).join(', ') ||
  'nothing in the feed';

/** CRM feeds Hive pulls venues from, for CRMs that cannot call the integration API. */
export function CrmFeedsCard() {
  const now = useNow(30_000);
  const sources = useLoad(async () => (await api.GET('/v1/integrations/crm-sources')).data);
  const [open, setOpen] = useState<{ kind: 'edit'; source?: CrmSource } | { kind: 'result'; source: CrmSource; result: CrmSyncResult } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(source: CrmSource, dryRun: boolean) {
    setBusy(source.id);
    setError(null);
    const { data, error: err } = await api.POST('/v1/integrations/crm-sources/{sourceId}/run', { params: { path: { sourceId: source.id } }, body: { dryRun } });
    setBusy(null);
    if (!data) return setError(errorMessage(err));
    if (!dryRun) void sources.reload();
    setOpen({ kind: 'result', source, result: data });
  }

  async function remove(source: CrmSource) {
    const { error: err } = await api.DELETE('/v1/integrations/crm-sources/{sourceId}', { params: { path: { sourceId: source.id } } });
    if (err) return setError(errorMessage(err));
    void sources.reload();
  }

  return (
    <Card className="mc-table-card">
      <div className="mc-toolbar">
        <span className="mc-section-title">
          <Icon name="swap" size={16} />
          &nbsp;CRM feeds
        </span>
        <Button size="sm" variant="primary" onClick={() => setOpen({ kind: 'edit' })}>
          <Icon name="plus" size={16} />
          Add feed
        </Button>
      </div>
      {error && <p className="mc-note mc-note--bad mc-pad-note">{error}</p>}
      <ul className="mc-rows mc-pad">
        {(sources.data ?? []).map((s) => (
          <li key={s.id} className="mc-rows__row">
            <span className="mc-grow">
              <span className="mc-strong mc-wrap">
                {s.name} · <code className="mc-small">{s.url}</code>
              </span>
              <span className="mc-small hv-muted">
                {s.format.toUpperCase()} every {s.intervalMinutes} min{s.hasAuth ? ' · with auth header' : ''}
                {s.lastRunAt ? ` · last pulled ${ago(s.lastRunAt, now)} ago: ${s.lastCounts ? counts(s.lastCounts) : ''}` : ' · not pulled yet'}
                {s.lastMessage ? ` · ${s.lastMessage}` : ''}
              </span>
            </span>
            {!s.active ? (
              <StatusPill status="idle">Paused</StatusPill>
            ) : s.lastStatus ? (
              <StatusPill status={STATUS[s.lastStatus].status}>{STATUS[s.lastStatus].label}</StatusPill>
            ) : (
              <StatusPill status="idle">Waiting</StatusPill>
            )}
            <Button variant="link" onClick={() => run(s, true)} disabled={busy === s.id}>
              Preview
            </Button>
            <Button variant="link" onClick={() => run(s, false)} disabled={busy === s.id}>
              {busy === s.id ? 'Pulling…' : 'Pull now'}
            </Button>
            <Button variant="link" onClick={() => setOpen({ kind: 'edit', source: s })}>
              Edit
            </Button>
            <Button variant="link" className="mc-link-danger" onClick={() => remove(s)}>
              Remove
            </Button>
          </li>
        ))}
        {sources.data && sources.data.length === 0 && (
          <li className="mc-table__empty">For CRMs that cannot call Hive: point Hive at a JSON or CSV export and it pulls on a schedule.</li>
        )}
      </ul>

      {open?.kind === 'edit' && (
        <FeedDialog
          source={open.source}
          onClose={() => setOpen(null)}
          onSaved={() => {
            void sources.reload();
            setOpen(null);
          }}
        />
      )}
      {open?.kind === 'result' && (
        <Dialog
          titleId="pull-title"
          title={open.result.dryRun ? 'Preview' : 'Pulled'}
          subtitle={`${open.source.name}: ${open.result.message ?? counts(open.result.counts)}`}
          onClose={() => setOpen(null)}
          width={760}
          footer={
            open.result.dryRun && open.result.status !== 'failed' ? (
              <>
                <Button onClick={() => setOpen(null)}>Close</Button>
                <Button variant="primary" onClick={() => run(open.source, false)}>
                  Pull now
                </Button>
              </>
            ) : (
              <Button variant="primary" onClick={() => setOpen(null)}>
                Done
              </Button>
            )
          }
        >
          <table className="mc-table mc-table--compact">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Venue</th>
                <th scope="col">Result</th>
                <th scope="col">Detail</th>
              </tr>
            </thead>
            <tbody>
              {open.result.rows.map((r) => (
                <tr key={r.index}>
                  <td>{r.index}</td>
                  <td>
                    {r.name ?? '—'}
                    {r.externalRef && <span className="mc-tiny hv-muted mc-line">{r.externalRef}</span>}
                  </td>
                  <td className={r.action === 'error' ? 'mc-bad' : r.action === 'unchanged' ? 'hv-muted' : 'mc-good'}>
                    {{ create: 'New', update: 'Update', unchanged: 'No change', error: 'Problem' }[r.action]}
                  </td>
                  <td className="mc-small">{r.message ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Dialog>
      )}
    </Card>
  );
}

function FeedDialog({ source, onClose, onSaved }: { source?: CrmSource; onClose(): void; onSaved(): void }) {
  const [name, setName] = useState(source?.name ?? '');
  const [url, setUrl] = useState(source?.url ?? 'https://');
  const [format, setFormat] = useState<CrmSource['format']>(source?.format ?? 'json');
  const [auth, setAuth] = useState('');
  const [clearAuth, setClearAuth] = useState(false);
  const [every, setEvery] = useState(String(source?.intervalMinutes ?? 60));
  const [active, setActive] = useState(source?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const minutes = Number(every);
    if (!Number.isInteger(minutes) || minutes < 15 || minutes > 1440) return setError('Pull every 15 to 1,440 minutes.');
    setBusy(true);
    setError(null);
    const base = { name: name.trim(), url: url.trim(), format, intervalMinutes: minutes };
    const result = source
      ? await api.PATCH('/v1/integrations/crm-sources/{sourceId}', {
          params: { path: { sourceId: source.id } },
          body: { ...base, active, ...(auth.trim() ? { authHeader: auth.trim() } : clearAuth ? { authHeader: '' } : {}) },
        })
      : await api.POST('/v1/integrations/crm-sources', { body: { ...base, ...(auth.trim() ? { authHeader: auth.trim() } : {}) } });
    setBusy(false);
    if (result.error) return setError(errorMessage(result.error));
    onSaved();
  }

  return (
    <Dialog
      titleId="feed-title"
      title={source ? `Edit ${source.name}` : 'Add a CRM feed'}
      subtitle="Hive fetches this address on a schedule and applies each venue, matched on its externalRef."
      onClose={onClose}
      width={600}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="feed-form" disabled={busy}>
            {source ? 'Save' : 'Add feed'}
          </Button>
        </>
      }
    >
      <form id="feed-form" className="mc-form" onSubmit={submit}>
        {error && <ErrorNotice title={error} />}
        <div className="mc-grid2">
          <Field id="f-name" label="Name" placeholder="Legacy CRM" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} />
          <div className="hv-field">
            <label className="hv-field__label" htmlFor="f-format">
              Format
            </label>
            <select id="f-format" className="hv-input" value={format} onChange={(e) => setFormat(e.target.value as CrmSource['format'])}>
              <option value="json">JSON (venues like the integration API)</option>
              <option value="csv">CSV (import columns plus externalRef)</option>
            </select>
          </div>
        </div>
        <Field id="f-url" label="Feed address" type="url" value={url} onChange={(e) => setUrl(e.target.value)} required maxLength={2000} />
        <div className="mc-grid2">
          <Field
            id="f-auth"
            label={source?.hasAuth ? 'Authorization header (stored; type to replace)' : 'Authorization header (optional)'}
            placeholder="Bearer …"
            type="password"
            autoComplete="off"
            value={auth}
            onChange={(e) => setAuth(e.target.value)}
            maxLength={2000}
          />
          <Field id="f-interval" label="Pull every (minutes)" inputMode="numeric" value={every} onChange={(e) => setEvery(e.target.value)} />
        </div>
        {source?.hasAuth && <Toggle label="Remove the stored header" checked={clearAuth} onChange={setClearAuth} />}
        {source && <Toggle label={active ? 'Active' : 'Paused'} checked={active} onChange={setActive} />}
      </form>
    </Dialog>
  );
}
